import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CopyPlus, Download, Plus, Save, Trash2 } from 'lucide-react';
import { Button, Field, Input, Select } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { fetchCurrentUser } from '@/lib/auth';
import { formatCPF, formatName } from '@/lib/format';
import { useCliente, useUpdateCliente } from '@/modules/clientes/api';
import { decodeClienteId } from '@/modules/clientes/cliente-id';

type OrcamentoItem = {
  item: string;
  servico: string;
  descricao: string;
  valor: string;
};

type OrcamentoStatus = 'rascunho' | 'enviado' | 'nao_aceito' | 'aceito' | 'substituido';
type OrcamentoDocumentVariant = 'tecnico' | 'comercial';
type OrcamentoCondicaoEspecial = {
  enabled: boolean;
  situacoes: string[];
  desconto_valor: string;
  desconto_percentual: string;
  valor_final: string;
  condicoes_pagamento: string;
  parcelas: string;
  observacoes: string;
  approved_by_email: string;
  approved_by_name: string;
  opened_at: string;
  financeiro_confirmado_em: string;
  financeiro_confirmado_por: string;
  admin_confirmado_em: string;
  admin_confirmado_por: string;
};

type OrcamentoVersao = {
  id: string;
  numero: number;
  ano: number;
  versao: number;
  status: OrcamentoStatus;
  motivo_versao: string;
  created_at: string;
  updated_at: string;
  titulo: string;
  intro: string;
  objeto_proposta: string;
  justificativa_contratacao: string;
  confidencialidade: string;
  items: OrcamentoItem[];
  condicoes_pagamento: string;
  validade_texto: string;
  prazo_execucao: string;
  exclusoes: string;
  consideracoes: string;
  responsavel_nome: string;
  responsavel_titulos: string;
  responsavel_registro: string;
  cliente_nome: string;
  cliente_cpf: string;
  document_variant?: OrcamentoDocumentVariant;
  condicao_especial: OrcamentoCondicaoEspecial;
};

type OrcamentoStore = {
  current_version_id: string;
  versions: OrcamentoVersao[];
};

const STATUS_OPTIONS: Array<{ value: OrcamentoStatus; label: string }> = [
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'nao_aceito', label: 'Não aceito' },
  { value: 'aceito', label: 'Aceito' },
  { value: 'substituido', label: 'Substituído' },
];

const defaultItem = (index: number): OrcamentoItem => ({
  item: String(index + 1).padStart(2, '0'),
  servico: '',
  descricao: '',
  valor: '',
});

function nowIso(): string {
  return new Date().toISOString();
}

function makeId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `orc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function padNumber(value: number): string {
  return String(Math.max(1, Math.trunc(value || 1))).padStart(3, '0');
}

function controlLabel(version: Pick<OrcamentoVersao, 'numero' | 'ano'>): string {
  return `${padNumber(version.numero)}/${version.ano}`;
}

function versionLabel(version: Pick<OrcamentoVersao, 'numero' | 'ano' | 'versao'>): string {
  return `${controlLabel(version)} · V${version.versao}`;
}

function buildBaseFields(cliente?: { nome?: string | null; cpf?: string | null }) {
  return {
    titulo: 'PROPOSTA COMERCIAL DE PRESTACAO DE SERVICOS TECNICOS AMBIENTAIS',
    intro:
      'Apresentamos a presente proposta comercial para prestacao de servicos tecnicos ambientais, destinada ao atendimento da demanda descrita abaixo.',
    objeto_proposta:
      'A presente proposta tem por finalidade a prestacao de servicos tecnicos especializados para subsidiar a apresentacao de defesa tecnica no Processo nº 1002969-73.2024.4.01.4103, com foco na analise ambiental e geoespacial do imovel denominado Fazenda Bandeirantes.\n\nOs servicos incluem analise documental, interpretacao de imagens geoespaciais, conferencia de poligonos, verificacao de sobreposicoes, caracterizacao ambiental do imovel e elaboracao de laudo tecnico destinado ao suporte da defesa processual.',
    justificativa_contratacao:
      'A contratacao se justifica pela necessidade de analise tecnica especializada sobre as informacoes ambientais e geoespaciais relacionadas ao imovel, considerando a existencia de processo judicial ambiental e a necessidade de demonstrar, de forma tecnica, a situacao ambiental e territorial da Fazenda Bandeirantes.\n\nA analise sera direcionada a interpretacao das imagens disponiveis, a verificacao da compatibilidade entre o imovel, os poligonos e os dados ambientais, bem como a producao de elementos tecnicos aptos a subsidiar a defesa.',
    confidencialidade:
      'As informacoes tecnicas, documentais, ambientais, fundiarias e processuais acessadas em razao da execucao desta proposta serao tratadas como confidenciais, sendo vedada sua divulgacao a terceiros sem autorizacao expressa do contratante, salvo quando necessaria ao cumprimento do objeto contratado ou por exigencia legal.',
    items: [defaultItem(0)],
    condicoes_pagamento: '50% na contratacao e 50% na entrega dos servicos',
    validade_texto: '10 dias',
    prazo_execucao: 'Conforme complexidade da demanda e disponibilizacao da documentacao necessaria',
    exclusoes:
      'Nao estao inclusos taxas, emolumentos, deslocamentos extraordinarios, despesas cartoriais e eventuais custos adicionais de campo, caso necessarios.',
    consideracoes:
      'Os servicos serao executados com base na documentacao disponibilizada e nas analises tecnicas cabiveis ao escopo proposto. Demandas complementares nao previstas poderao ser objeto de ajuste especifico.',
    responsavel_nome: 'Grasielly Cristina de Andrade',
    responsavel_titulos: 'Engenheira Agronoma e Engenheira de Seguranca do Trabalho',
    responsavel_registro: 'CREA/RO 7573-D',
    cliente_nome: formatName(cliente?.nome ?? '') || '',
    cliente_cpf: cliente?.cpf ?? '',
    condicao_especial: {
      enabled: false,
      situacoes: [],
      desconto_valor: '',
      desconto_percentual: '',
      valor_final: '',
      condicoes_pagamento: '',
      parcelas: '',
      observacoes: '',
      approved_by_email: '',
      approved_by_name: '',
      opened_at: '',
      financeiro_confirmado_em: '',
      financeiro_confirmado_por: '',
      admin_confirmado_em: '',
      admin_confirmado_por: '',
    } satisfies OrcamentoCondicaoEspecial,
  };
}

function sanitizeItems(raw: unknown): OrcamentoItem[] {
  if (!Array.isArray(raw)) return [defaultItem(0)];
  const items = raw.map((item, index) => {
    const row = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
    return {
      item: String(row.item ?? String(index + 1).padStart(2, '0')),
      servico: String(row.servico ?? ''),
      descricao: String(row.descricao ?? ''),
      valor: String(row.valor ?? ''),
    };
  });
  return items.length ? items : [defaultItem(0)];
}

function buildVersion(
  raw: unknown,
  cliente?: { nome?: string | null; cpf?: string | null },
  overrides?: Partial<OrcamentoVersao>,
): OrcamentoVersao {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const base = buildBaseFields(cliente);
  const createdAt = typeof src.created_at === 'string' ? src.created_at : nowIso();
  const updatedAt = typeof src.updated_at === 'string' ? src.updated_at : createdAt;
  return {
    id: String(src.id ?? makeId()),
    numero: Number(src.numero) > 0 ? Number(src.numero) : 1,
    ano: Number(src.ano) > 0 ? Number(src.ano) : new Date().getFullYear(),
    versao: Number(src.versao) > 0 ? Number(src.versao) : 1,
    status: (['rascunho', 'enviado', 'nao_aceito', 'aceito', 'substituido'] as const).includes(
      src.status as OrcamentoStatus,
    )
      ? (src.status as OrcamentoStatus)
      : 'rascunho',
    motivo_versao: String(src.motivo_versao ?? 'Versão inicial'),
    created_at: createdAt,
    updated_at: updatedAt,
    titulo: String(src.titulo ?? base.titulo),
    intro: String(src.intro ?? base.intro),
    objeto_proposta: String(src.objeto_proposta ?? base.objeto_proposta),
    justificativa_contratacao: String(src.justificativa_contratacao ?? base.justificativa_contratacao),
    confidencialidade: String(src.confidencialidade ?? base.confidencialidade),
    items: sanitizeItems(src.items),
    condicoes_pagamento: String(src.condicoes_pagamento ?? base.condicoes_pagamento),
    validade_texto: String(src.validade_texto ?? base.validade_texto),
    prazo_execucao: String(src.prazo_execucao ?? base.prazo_execucao),
    exclusoes: String(src.exclusoes ?? base.exclusoes),
    consideracoes: String(src.consideracoes ?? base.consideracoes),
    responsavel_nome: String(src.responsavel_nome ?? base.responsavel_nome),
    responsavel_titulos: String(src.responsavel_titulos ?? base.responsavel_titulos),
    responsavel_registro: String(src.responsavel_registro ?? base.responsavel_registro),
    cliente_nome: String(src.cliente_nome ?? base.cliente_nome),
    cliente_cpf: String(src.cliente_cpf ?? base.cliente_cpf),
    condicao_especial: {
      enabled:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? Boolean((src.condicao_especial as Record<string, unknown>).enabled)
          : base.condicao_especial.enabled,
      situacoes:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? Array.isArray((src.condicao_especial as Record<string, unknown>).situacoes)
            ? ((src.condicao_especial as Record<string, unknown>).situacoes as unknown[])
                .map((item) => String(item))
                .filter(Boolean)
            : base.condicao_especial.situacoes
          : base.condicao_especial.situacoes,
      desconto_valor:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).desconto_valor ?? '')
          : base.condicao_especial.desconto_valor,
      desconto_percentual:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).desconto_percentual ?? '')
          : base.condicao_especial.desconto_percentual,
      valor_final:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).valor_final ?? '')
          : base.condicao_especial.valor_final,
      condicoes_pagamento:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).condicoes_pagamento ?? '')
          : base.condicao_especial.condicoes_pagamento,
      parcelas:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).parcelas ?? '')
          : base.condicao_especial.parcelas,
      observacoes:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).observacoes ?? '')
          : base.condicao_especial.observacoes,
      approved_by_email:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).approved_by_email ?? '')
          : base.condicao_especial.approved_by_email,
      approved_by_name:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).approved_by_name ?? '')
          : base.condicao_especial.approved_by_name,
      opened_at:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).opened_at ?? '')
          : base.condicao_especial.opened_at,
      financeiro_confirmado_em:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).financeiro_confirmado_em ?? '')
          : base.condicao_especial.financeiro_confirmado_em,
      financeiro_confirmado_por:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).financeiro_confirmado_por ?? '')
          : base.condicao_especial.financeiro_confirmado_por,
      admin_confirmado_em:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).admin_confirmado_em ?? '')
          : base.condicao_especial.admin_confirmado_em,
      admin_confirmado_por:
        src.condicao_especial && typeof src.condicao_especial === 'object'
          ? String((src.condicao_especial as Record<string, unknown>).admin_confirmado_por ?? '')
          : base.condicao_especial.admin_confirmado_por,
    },
    ...overrides,
  };
}

function sanitizeStore(raw: unknown, cliente?: { nome?: string | null; cpf?: string | null }): OrcamentoStore {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const versionsRaw = Array.isArray(src.versions) ? src.versions : null;
  const versions = versionsRaw
    ? versionsRaw.map((version) => buildVersion(version, cliente))
    : [buildVersion(src, cliente)];
  const currentId =
    typeof src.current_version_id === 'string' && versions.some((version) => version.id === src.current_version_id)
      ? src.current_version_id
      : versions[0]!.id;
  return {
    current_version_id: currentId,
    versions,
  };
}

function moneyToNumber(value: string): number {
  const clean = value.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(clean);
  return Number.isFinite(n) ? n : 0;
}

function formatBRLInput(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function moneyToCurrencyString(value: string): string {
  const amount = moneyToNumber(value);
  return amount > 0 ? formatBRLInput(amount) : '';
}

function formatDateBR(value: Date): string {
  return value.toLocaleDateString('pt-BR', { timeZone: 'America/Manaus' });
}

function addDays(base: Date, days: number): Date {
  const copy = new Date(base);
  copy.setDate(copy.getDate() + days);
  return copy;
}

function buildCondicaoPagamentoParcelado(parcelas: string, valorTotal: number, dataBaseIso?: string): string {
  const totalParcelas = Number(parcelas);
  if (!Number.isFinite(totalParcelas) || totalParcelas < 1) return '';
  const dataBase = dataBaseIso ? new Date(dataBaseIso) : new Date();
  const dataReferencia = Number.isNaN(dataBase.getTime()) ? new Date() : dataBase;
  const totalPagamentos = totalParcelas + 1;
  const valorParcela = valorTotal > 0 ? formatBRLInput(valorTotal / totalPagamentos) : '';
  const datas = [
    `entrada em ${formatDateBR(dataReferencia)}`,
    ...Array.from(
      { length: totalParcelas },
      (_, idx) => `${idx + 1}ª parcela em ${formatDateBR(addDays(dataReferencia, (idx + 1) * 30))}`,
    ),
  ];
  const cronograma = datas.join(', ').replace(/, ([^,]+)$/, ' e $1');
  return valorParcela
    ? `${totalParcelas}x com entrada de ${valorParcela} e demais parcelas de ${valorParcela}: ${cronograma}.`
    : `${totalParcelas}x com ${cronograma}.`;
}

function hasCondicaoDesconto(condicao: OrcamentoCondicaoEspecial): boolean {
  return !!(condicao.desconto_valor.trim() || condicao.desconto_percentual.trim() || condicao.valor_final.trim());
}

function hasCondicaoParcelamento(condicao: OrcamentoCondicaoEspecial): boolean {
  return !!(condicao.condicoes_pagamento.trim() || condicao.parcelas.trim());
}

function hasSituacaoDesconto(condicao: OrcamentoCondicaoEspecial): boolean {
  return condicao.situacoes.includes('avista') || condicao.situacoes.includes('30dias');
}

const CONDICAO_SITUACOES = [
  {
    value: 'avista',
    label: 'Desconto para pagamento à vista',
    hint: 'Permite desconto em valor fixo ou percentual.',
  },
  {
    value: '30dias',
    label: 'Desconto para pagamento em até 30 dias',
    hint: 'Percentual limitado a 5,5%.',
  },
  {
    value: 'parcelado',
    label: 'Parcelamento',
    hint: 'Permite parcelamento de 3x até 10x.',
  },
] as const;

function cloneAsNewVersion(source: OrcamentoVersao): OrcamentoVersao {
  const now = nowIso();
  return buildVersion(source, undefined, {
    id: makeId(),
    numero: source.numero,
    ano: source.ano,
    versao: source.versao + 1,
    status: 'rascunho',
    motivo_versao: 'Nova versao criada a partir da proposta anterior',
    created_at: now,
    updated_at: now,
  });
}

export const Route = createFileRoute('/_authed/clientes_/$id_/relatorio/orcamento')({
  component: OrcamentoClienteRoute,
});

function OrcamentoClienteRoute() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const cpf = decodeClienteId(id);
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: 5 * 60_000,
  });
  const { data: cliente, isLoading, error } = useCliente(cpf);
  const update = useUpdateCliente(cpf);
  const [store, setStore] = useState<OrcamentoStore>({
    current_version_id: '',
    versions: [buildVersion({}, undefined)],
  });
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [showCondicaoEspecial, setShowCondicaoEspecial] = useState(false);

  useEffect(() => {
    if (!cliente) return;
    const nextStore = sanitizeStore((cliente as Record<string, unknown>).orcamento_payload, cliente);
    setStore(nextStore);
    setSelectedVersionId(nextStore.current_version_id);
  }, [cliente]);

  const selectedVersion = useMemo(
    () => store.versions.find((version) => version.id === selectedVersionId) ?? store.versions[0],
    [store, selectedVersionId],
  );

  const orderedVersions = useMemo(
    () =>
      [...store.versions].sort((a, b) => {
        if (a.ano !== b.ano) return b.ano - a.ano;
        if (a.numero !== b.numero) return b.numero - a.numero;
        return b.versao - a.versao;
      }),
    [store.versions],
  );

  const total = useMemo(
    () => (selectedVersion ? selectedVersion.items.reduce((sum, item) => sum + moneyToNumber(item.valor), 0) : 0),
    [selectedVersion],
  );

  const firstSendOccurred = useMemo(
    () => store.versions.some((version) => ['enviado', 'nao_aceito', 'aceito'].includes(version.status)),
    [store.versions],
  );

  const valoresTravados = firstSendOccurred;

  const naoAceitoCount = useMemo(
    () => store.versions.filter((version) => version.status === 'nao_aceito').length,
    [store.versions],
  );

  const condicaoEspecialLiberada = naoAceitoCount >= 2;
  const isAdmin = me?.role === 'admin';
  const isFinanceiro = String(me?.email || '').toLowerCase() === 'cleiciane.resky@ruralplan.com.br';
  const canManageCondicaoEspecial =
    isAdmin || isFinanceiro;

  const isEditable = !!selectedVersion && selectedVersion.id === store.current_version_id && selectedVersion.status === 'rascunho';

  const back = (
    <Button
      variant="ghost"
      onClick={() => {
        if (window.history.length > 1) window.history.back();
        else
          void navigate({
            to: '/clientes/$id',
            params: { id },
            search: { relatorio: undefined, tab: 'relatorios', from_pipe: undefined, from_grupo: undefined },
          });
      }}
    >
      <ArrowLeft size={14} strokeWidth={1.5} />
      Voltar ao cliente
    </Button>
  );

  function updateSelectedVersion(patch: Partial<OrcamentoVersao>) {
    if (!selectedVersion) return;
    setStore((current) => ({
      ...current,
      versions: current.versions.map((version) =>
        version.id === selectedVersion.id
          ? { ...version, ...patch, updated_at: nowIso() }
          : version,
      ),
    }));
  }

  function updateSelectedItem(index: number, patch: Partial<OrcamentoItem>) {
    if (!selectedVersion) return;
    updateSelectedVersion({
      items: selectedVersion.items.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    });
  }

  function updateCondicaoEspecial(patch: Partial<OrcamentoCondicaoEspecial>) {
    if (!selectedVersion) return;
    const nextCondicao = {
      ...selectedVersion.condicao_especial,
      ...patch,
    };
    updateSelectedVersion({
      condicao_especial: nextCondicao,
      ...(typeof patch.condicoes_pagamento === 'string' && patch.condicoes_pagamento.trim()
        ? { condicoes_pagamento: patch.condicoes_pagamento }
        : {}),
    });
  }

  function selectCondicaoSituacao(value: string) {
    if (!selectedVersion) return;
    const atual = selectedVersion.condicao_especial.situacoes[0] || '';
    const nextSituacoes = atual === value ? [] : [value];
    const patch: Partial<OrcamentoCondicaoEspecial> = {
      enabled: nextSituacoes.length > 0,
      situacoes: nextSituacoes,
      ...resetCondicaoConfirmacoes(),
    };
    if (!nextSituacoes.includes('avista') && !nextSituacoes.includes('30dias')) {
      patch.desconto_valor = '';
      patch.desconto_percentual = '';
      patch.valor_final = '';
    }
    if (nextSituacoes.includes('30dias')) {
      patch.desconto_valor = '';
    }
    if (!nextSituacoes.includes('parcelado')) {
      patch.condicoes_pagamento = '';
      patch.parcelas = '';
    }
    updateCondicaoEspecial(patch);
  }

  function resetCondicaoConfirmacoes() {
    return {
      financeiro_confirmado_em: '',
      financeiro_confirmado_por: '',
      admin_confirmado_em: '',
      admin_confirmado_por: '',
    } satisfies Partial<OrcamentoCondicaoEspecial>;
  }

  function validateCondicaoEspecial(version: OrcamentoVersao | undefined): string | null {
    if (!version) return null;
    const condicao = version.condicao_especial;
    const hasDesconto = hasCondicaoDesconto(condicao);
    const hasParcelamento = hasCondicaoParcelamento(condicao);
    if (condicao.situacoes.includes('30dias') && condicao.desconto_valor.trim()) {
      return 'Para pagamento em até 30 dias, informe apenas desconto percentual.';
    }
    if (condicao.situacoes.includes('30dias') && Number(condicao.desconto_percentual.replace(',', '.')) > 5.5) {
      return 'O desconto para pagamento em até 30 dias não pode ultrapassar 5,5%.';
    }
    if (condicao.situacoes.includes('parcelado')) {
      const parcelas = Number(condicao.parcelas);
      if (!Number.isFinite(parcelas) || parcelas < 3 || parcelas > 10) {
        return 'O parcelamento deve ficar entre 3x e 10x.';
      }
    }
    if (!(hasDesconto && hasParcelamento)) return null;
    if (!condicao.financeiro_confirmado_em || !condicao.admin_confirmado_em) {
      return 'Para conceder desconto e parcelamento ao mesmo tempo, é obrigatória a confirmação do financeiro e do admin.';
    }
    return null;
  }

  async function persistStore(nextStore: OrcamentoStore, successMessage = 'Orçamento salvo com sucesso.') {
    await update.mutateAsync({ orcamento_payload: nextStore });
    setFeedback(successMessage);
  }

  function materializeNewVersion(source: OrcamentoVersao) {
    const nextVersion = cloneAsNewVersion(source);
    const nextStore: OrcamentoStore = {
      current_version_id: nextVersion.id,
      versions: store.versions.map((version) =>
        version.id === source.id && version.status === 'rascunho'
          ? { ...version, status: 'substituido' as OrcamentoStatus, updated_at: nowIso() }
          : version,
      ).concat(nextVersion),
    };
    setStore(nextStore);
    setSelectedVersionId(nextVersion.id);
    setFeedback(`Nova versão criada: ${versionLabel(nextVersion)}.`);
    return nextStore;
  }

  async function handleSave() {
    if (!selectedVersion) return;
    setFeedback(null);
    const validationError = validateCondicaoEspecial(selectedVersion);
    if (validationError) {
      setFeedback(validationError);
      return;
    }
    const nextStore = store;
    await persistStore(nextStore, `Versão ${versionLabel(selectedVersion)} salva com sucesso.`);
  }

  async function handleGeneratePdf(variant: OrcamentoDocumentVariant) {
    if (!cliente || !selectedVersion) return;
    setPdfBusy(true);
    setFeedback(null);
    try {
      const validationError = validateCondicaoEspecial(selectedVersion);
      if (validationError) {
        setFeedback(validationError);
        return;
      }
      const condicaoParceladaAtiva =
        selectedVersion.condicao_especial.situacoes.includes('parcelado') &&
        selectedVersion.condicao_especial.parcelas.trim();
      const condicaoPagamentoAtual = condicaoParceladaAtiva
        ? buildCondicaoPagamentoParcelado(selectedVersion.condicao_especial.parcelas, total, selectedVersion.created_at)
        : selectedVersion.condicao_especial.condicoes_pagamento.trim();
      const versionForPdf = {
        ...selectedVersion,
        condicoes_pagamento: condicaoPagamentoAtual || selectedVersion.condicoes_pagamento,
        condicao_especial: {
          ...selectedVersion.condicao_especial,
          ...(condicaoPagamentoAtual ? { condicoes_pagamento: condicaoPagamentoAtual } : {}),
        },
        document_variant: variant,
        status: selectedVersion.status === 'rascunho' ? 'enviado' : selectedVersion.status,
        updated_at: nowIso(),
      } satisfies OrcamentoVersao;

      const nextStore: OrcamentoStore = {
        ...store,
        versions: store.versions.map((version) =>
          version.id === selectedVersion.id ? versionForPdf : version,
        ),
      };

      setStore(nextStore);
      await update.mutateAsync({ orcamento_payload: nextStore });

      const downloadUrl = `/api/v2/clientes/${encodeURIComponent(cpf)}/orcamento-pdf?variant=${variant}&t=${Date.now()}`;
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setFeedback(
        `PDF ${variant === 'comercial' ? 'comercial' : 'técnico'} gerado com sucesso para ${versionLabel(versionForPdf)}.`,
      );
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Falha ao gerar PDF.');
    } finally {
      setPdfBusy(false);
    }
  }

  if (isLoading) {
    return (
      <PageShell eyebrow="Relatórios" title="Orçamento" back={back}>
        <p className="text-sm text-text-muted">Carregando…</p>
      </PageShell>
    );
  }

  if (error || !cliente || !selectedVersion) {
    return (
      <PageShell eyebrow="Relatórios" title="Orçamento" back={back}>
        <p className="text-sm text-danger">
          {error instanceof ApiError ? error.message : 'Cliente não encontrado.'}
        </p>
      </PageShell>
    );
  }

  return (
    <PageShell
      eyebrow="Relatórios"
      title="Orçamento comercial"
      back={back}
      lead={`${formatName(cliente.nome)} · ${formatCPF(cliente.cpf)}`}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              materializeNewVersion(selectedVersion);
            }}
            disabled={pdfBusy}
          >
            <CopyPlus size={14} strokeWidth={1.5} />
            Nova versão
          </Button>
          <Button variant="secondary" onClick={() => void handleSave()} disabled={update.isPending || pdfBusy}>
            <Save size={14} strokeWidth={1.5} />
            {update.isPending ? 'Salvando…' : 'Salvar versão'}
          </Button>
          <Button variant="secondary" onClick={() => void handleGeneratePdf('tecnico')} disabled={pdfBusy}>
            <Download size={14} strokeWidth={1.5} />
            {pdfBusy ? 'Gerando PDF…' : 'Emitir técnica'}
          </Button>
          <Button variant="primary" onClick={() => void handleGeneratePdf('comercial')} disabled={pdfBusy}>
            <Download size={14} strokeWidth={1.5} />
            {pdfBusy ? 'Gerando PDF…' : 'Emitir comercial'}
          </Button>
        </div>
      }
    >
      <Stack gap="md">
        {feedback && (
          <p className={feedback.includes('sucesso') ? 'text-sm text-[var(--ok)]' : 'text-sm text-danger'}>
            {feedback}
          </p>
        )}

        <section className="rounded-2xl border border-border bg-elevated p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="grid gap-4 md:grid-cols-[1.1fr_0.9fr]">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-border bg-surface px-4 py-3">
                <p className="text-[0.68rem] uppercase tracking-[0.12em] text-text-muted">Controle</p>
                <p className="mt-1 text-base font-semibold text-text">{controlLabel(selectedVersion)}</p>
              </div>
              <div className="rounded-xl border border-border bg-surface px-4 py-3">
                <p className="text-[0.68rem] uppercase tracking-[0.12em] text-text-muted">Versão</p>
                <p className="mt-1 text-base font-semibold text-text">V{selectedVersion.versao}</p>
              </div>
              <div className="rounded-xl border border-border bg-surface px-4 py-3">
                <p className="text-[0.68rem] uppercase tracking-[0.12em] text-text-muted">Status</p>
                <Select
                  value={selectedVersion.status}
                  onChange={(e) => updateSelectedVersion({ status: e.target.value as OrcamentoStatus })}
                  disabled={update.isPending || pdfBusy}
                  className="mt-1"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface px-4 py-3">
              <p className="text-[0.68rem] uppercase tracking-[0.12em] text-text-muted">Histórico de versões</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {orderedVersions.map((version) => {
                  const active = version.id === selectedVersion.id;
                  return (
                    <button
                      key={version.id}
                      type="button"
                      onClick={() => setSelectedVersionId(version.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                        active
                          ? 'border-accent bg-[rgba(217,119,87,0.12)] text-text'
                          : 'border-border bg-elevated text-text-muted hover:text-text'
                      }`}
                    >
                      {versionLabel(version)} · {STATUS_OPTIONS.find((option) => option.value === version.status)?.label ?? version.status}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {!isEditable && (
            <p className="mt-4 text-sm text-text-muted">
              Esta versão está preservada para histórico. Para alterar conteúdo, use <b>Nova versão</b>.
            </p>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-elevated p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold text-text">Condição especial</h2>
              <p className="text-xs text-text-muted">
                Área protegida para negociação excepcional. Só libera após o segundo <b>Não aceito</b>.
              </p>
            </div>
            <Button
              variant="secondary"
              disabled={!condicaoEspecialLiberada}
              onClick={() => {
                const nextOpen = !showCondicaoEspecial;
                setShowCondicaoEspecial(nextOpen);
                if (
                  nextOpen &&
                  !selectedVersion.condicao_especial.opened_at &&
                  condicaoEspecialLiberada &&
                  canManageCondicaoEspecial
                ) {
                  updateCondicaoEspecial({
                    opened_at: nowIso(),
                    approved_by_email: me?.email ?? '',
                    approved_by_name: me?.name ?? '',
                  });
                }
              }}
            >
              {showCondicaoEspecial ? 'Ocultar condição especial' : 'Abrir condição especial'}
            </Button>
          </div>

          {!condicaoEspecialLiberada && (
            <p className="mt-4 rounded-xl border border-border bg-surface px-4 py-3 text-sm text-text-muted">
              A condição especial ficará disponível somente após registrar <b>2 versões</b> com status <b>Não aceito</b>.
              Hoje: <b>{naoAceitoCount}</b>.
            </p>
          )}

          {condicaoEspecialLiberada && !canManageCondicaoEspecial && (
            <p className="mt-4 rounded-xl border border-border bg-surface px-4 py-3 text-sm text-text-muted">
              <b>Condição especial liberada, aguardando acesso do financeiro.</b>
              {' '}
              A visualização já está disponível, mas a gravação só pode ser feita por
              {' '}
              <b>cleiciane.resky@ruralplan.com.br</b>
              {' '}
              ou por um usuário administrador.
            </p>
          )}

          {showCondicaoEspecial && condicaoEspecialLiberada && (
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-surface px-4 py-3">
                <p className="text-[0.68rem] uppercase tracking-[0.12em] text-text-muted">Valor original</p>
                <p className="mt-1 text-base font-semibold text-text">{formatBRLInput(total)}</p>
              </div>
              <div className="rounded-xl border border-border bg-surface px-4 py-3">
                <p className="text-[0.68rem] uppercase tracking-[0.12em] text-text-muted">Liberação</p>
                <p className="mt-1 text-sm text-text">
                  {selectedVersion.condicao_especial.approved_by_name || me?.name || 'Financeiro'}
                </p>
                <p className="text-xs text-text-muted">
                  {selectedVersion.condicao_especial.approved_by_email || me?.email || 'cleiciane.resky@ruralplan.com.br'}
                </p>
              </div>

              <div className="md:col-span-2 rounded-xl border border-border bg-surface px-4 py-3">
                <p className="text-sm font-medium text-text">Situação da condição especial</p>
                <div className="mt-3 grid gap-3">
                  {CONDICAO_SITUACOES.map((opcao) => (
                    <label key={opcao.value} className="flex items-start gap-3 text-sm text-text">
                      <input
                        type="radio"
                        name="condicao-especial-situacao"
                        checked={selectedVersion.condicao_especial.situacoes.includes(opcao.value)}
                        disabled={!isEditable || !canManageCondicaoEspecial}
                        onChange={() => selectCondicaoSituacao(opcao.value)}
                      />
                      <span>
                        <span className="font-medium">{opcao.label}</span>
                        <span className="block text-xs text-text-muted">{opcao.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {hasSituacaoDesconto(selectedVersion.condicao_especial) && (
                <>
                  {selectedVersion.condicao_especial.situacoes.includes('avista') && (
                    <Field label="Desconto em valor fixo" htmlFor="orc-condicao-desconto">
                      <Input
                        id="orc-condicao-desconto"
                        value={selectedVersion.condicao_especial.desconto_valor}
                        disabled={!isEditable || !canManageCondicaoEspecial}
                        placeholder="R$ 0,00"
                        onChange={(e) => {
                          const desconto = e.target.value;
                          const valorFinal = Math.max(total - moneyToNumber(desconto), 0);
                          updateCondicaoEspecial({
                            enabled: true,
                            desconto_valor: desconto,
                            valor_final: valorFinal > 0 ? formatBRLInput(valorFinal) : '',
                            approved_by_email: selectedVersion.condicao_especial.approved_by_email || me?.email || '',
                            approved_by_name: selectedVersion.condicao_especial.approved_by_name || me?.name || '',
                            ...resetCondicaoConfirmacoes(),
                          });
                        }}
                      />
                    </Field>
                  )}
                  <Field label="Desconto percentual" htmlFor="orc-condicao-percentual">
                    <Input
                      id="orc-condicao-percentual"
                      value={selectedVersion.condicao_especial.desconto_percentual}
                      disabled={!isEditable || !canManageCondicaoEspecial}
                      placeholder={
                        selectedVersion.condicao_especial.situacoes.includes('30dias') ? 'Máx. 5,5%' : '0,00%'
                      }
                      onChange={(e) =>
                        updateCondicaoEspecial({
                          enabled: true,
                          desconto_percentual: e.target.value,
                          ...resetCondicaoConfirmacoes(),
                        })
                      }
                    />
                  </Field>
                  <Field
                    label="Valor final aprovado"
                    htmlFor="orc-condicao-final"
                    className={selectedVersion.condicao_especial.situacoes.includes('avista') ? 'md:col-span-2' : undefined}
                  >
                    <Input
                      id="orc-condicao-final"
                      value={selectedVersion.condicao_especial.valor_final}
                      disabled={!isEditable || !canManageCondicaoEspecial}
                      placeholder="R$ 0,00"
                      onChange={(e) =>
                        updateCondicaoEspecial({
                          enabled: true,
                          valor_final: moneyToCurrencyString(e.target.value) || e.target.value,
                          ...resetCondicaoConfirmacoes(),
                        })
                      }
                    />
                  </Field>
                </>
              )}

              {selectedVersion.condicao_especial.situacoes.includes('parcelado') && (
                <>
                  <Field label="Quantidade de parcelas" htmlFor="orc-condicao-parcelas">
                    <Select
                      id="orc-condicao-parcelas"
                      value={selectedVersion.condicao_especial.parcelas}
                      disabled={!isEditable || !canManageCondicaoEspecial}
                      onChange={(e) =>
                        updateCondicaoEspecial({
                          enabled: true,
                          parcelas: e.target.value,
                          condicoes_pagamento: buildCondicaoPagamentoParcelado(
                            e.target.value,
                            total,
                            selectedVersion.created_at,
                          ),
                          ...resetCondicaoConfirmacoes(),
                        })
                      }
                    >
                      <option value="">Selecionar</option>
                      {Array.from({ length: 8 }, (_, idx) => idx + 3).map((n) => (
                        <option key={n} value={String(n)}>
                          {n}x
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Condição especial de pagamento" htmlFor="orc-condicao-pagamento">
                    <Input
                      id="orc-condicao-pagamento"
                      value={selectedVersion.condicao_especial.condicoes_pagamento}
                      disabled={!isEditable || !canManageCondicaoEspecial}
                      placeholder="Ex.: 6x fixas"
                      onChange={(e) =>
                        updateCondicaoEspecial({
                          enabled: true,
                          condicoes_pagamento: e.target.value,
                          ...resetCondicaoConfirmacoes(),
                        })
                      }
                    />
                  </Field>
                </>
              )}

              <Field label="Observações do financeiro" htmlFor="orc-condicao-obs" className="md:col-span-2">
                <textarea
                  id="orc-condicao-obs"
                  className="input min-h-[100px] resize-y rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text"
                  value={selectedVersion.condicao_especial.observacoes}
                  disabled={!isEditable || !canManageCondicaoEspecial}
                  placeholder="Registrar motivo, limite aprovado e orientação para fechamento."
                  onChange={(e) =>
                    updateCondicaoEspecial({
                      enabled: true,
                      observacoes: e.target.value,
                      ...resetCondicaoConfirmacoes(),
                    })
                  }
                />
              </Field>

              {hasCondicaoDesconto(selectedVersion.condicao_especial) &&
                hasCondicaoParcelamento(selectedVersion.condicao_especial) && (
                  <div className="md:col-span-2 rounded-xl border border-border bg-surface px-4 py-3">
                    <p className="text-sm font-medium text-text">
                      Desconto + parcelamento juntos exigem confirmação do financeiro e do admin.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <Button
                        variant="secondary"
                        disabled={!isEditable || !isFinanceiro}
                        onClick={() =>
                          updateCondicaoEspecial({
                            financeiro_confirmado_em: nowIso(),
                            financeiro_confirmado_por: me?.email || 'financeiro',
                          })
                        }
                      >
                        Confirmar financeiro
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={!isEditable || !isAdmin}
                        onClick={() =>
                          updateCondicaoEspecial({
                            admin_confirmado_em: nowIso(),
                            admin_confirmado_por: me?.email || 'admin',
                          })
                        }
                      >
                        Confirmar admin
                      </Button>
                    </div>
                    <div className="mt-3 text-xs text-text-muted">
                      <div>
                        Financeiro:{' '}
                        {selectedVersion.condicao_especial.financeiro_confirmado_em
                          ? `confirmado por ${selectedVersion.condicao_especial.financeiro_confirmado_por}`
                          : 'pendente'}
                      </div>
                      <div>
                        Admin:{' '}
                        {selectedVersion.condicao_especial.admin_confirmado_em
                          ? `confirmado por ${selectedVersion.condicao_especial.admin_confirmado_por}`
                          : 'pendente'}
                      </div>
                    </div>
                  </div>
                )}

              {!canManageCondicaoEspecial && (
                <p className="md:col-span-2 text-xs text-text-muted">
                  Visualização liberada. Alterações e gravação da condição especial são permitidas apenas para o financeiro ou administrador.
                </p>
              )}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-elevated p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Título da proposta" htmlFor="orc-titulo" className="md:col-span-2">
              <Input
                id="orc-titulo"
                value={selectedVersion.titulo}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ titulo: e.target.value })}
              />
            </Field>
            <Field label="Cliente" htmlFor="orc-cliente-nome">
              <Input
                id="orc-cliente-nome"
                value={selectedVersion.cliente_nome}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ cliente_nome: e.target.value })}
              />
            </Field>
            <Field label="CPF do cliente" htmlFor="orc-cliente-cpf">
              <Input
                id="orc-cliente-cpf"
                value={selectedVersion.cliente_cpf}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ cliente_cpf: e.target.value })}
              />
            </Field>
            <Field label="Introdução" htmlFor="orc-intro" className="md:col-span-2">
              <textarea
                id="orc-intro"
                className="input min-h-[110px] resize-y rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text"
                value={selectedVersion.intro}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ intro: e.target.value })}
              />
            </Field>
            <Field label="1. Objeto da proposta" htmlFor="orc-objeto" className="md:col-span-2">
              <textarea
                id="orc-objeto"
                className="input min-h-[150px] resize-y rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text"
                value={selectedVersion.objeto_proposta}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ objeto_proposta: e.target.value })}
              />
            </Field>
            <Field label="Justificativa técnica da contratação" htmlFor="orc-justificativa" className="md:col-span-2">
              <textarea
                id="orc-justificativa"
                className="input min-h-[150px] resize-y rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text"
                value={selectedVersion.justificativa_contratacao}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ justificativa_contratacao: e.target.value })}
              />
            </Field>
            <Field label="Confidencialidade" htmlFor="orc-confidencialidade" className="md:col-span-2">
              <textarea
                id="orc-confidencialidade"
                className="input min-h-[120px] resize-y rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text"
                value={selectedVersion.confidencialidade}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ confidencialidade: e.target.value })}
              />
            </Field>
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-elevated p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-sm font-semibold text-text">Itens do orçamento</h2>
              <p className="text-xs text-text-muted">A proposta nunca é excluída: alterações devem gerar nova versão.</p>
            </div>
            <Button
              variant="ghost"
              disabled={!isEditable || valoresTravados}
              onClick={() =>
                updateSelectedVersion({ items: [...selectedVersion.items, defaultItem(selectedVersion.items.length)] })
              }
            >
              <Plus size={14} strokeWidth={1.5} />
              Adicionar item
            </Button>
          </div>

          <div className="flex flex-col gap-4">
            {selectedVersion.items.map((item, index) => (
              <div key={`${index}-${item.item}`} className="rounded-xl border border-border bg-surface px-4 py-4">
                <div className="grid gap-4 md:grid-cols-[90px_1fr_180px]">
                  <Field label="Item" htmlFor={`orc-item-${index}`}>
                    <Input
                      id={`orc-item-${index}`}
                      value={item.item}
                      disabled={!isEditable}
                      onChange={(e) => updateSelectedItem(index, { item: e.target.value })}
                    />
                  </Field>
                  <Field label="Serviço" htmlFor={`orc-servico-${index}`}>
                    <Input
                      id={`orc-servico-${index}`}
                      value={item.servico}
                      disabled={!isEditable}
                      onChange={(e) => updateSelectedItem(index, { servico: e.target.value })}
                    />
                  </Field>
                  <Field label="Valor" htmlFor={`orc-valor-${index}`}>
                    <Input
                      id={`orc-valor-${index}`}
                      value={item.valor}
                      placeholder="R$ 0,00"
                      disabled={!isEditable || valoresTravados}
                      onChange={(e) => updateSelectedItem(index, { valor: e.target.value })}
                    />
                  </Field>
                  <Field label="Descrição objetiva" htmlFor={`orc-desc-${index}`} className="md:col-span-3">
                    <textarea
                      id={`orc-desc-${index}`}
                      className="input min-h-[92px] resize-y rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text"
                      value={item.descricao}
                      disabled={!isEditable}
                      onChange={(e) => updateSelectedItem(index, { descricao: e.target.value })}
                    />
                  </Field>
                </div>
                {isEditable && !valoresTravados && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      variant="ghost"
                      className="text-danger hover:text-danger"
                      onClick={() =>
                        updateSelectedVersion({
                          items:
                            selectedVersion.items.length > 1
                              ? selectedVersion.items.filter((_, rowIndex) => rowIndex !== index)
                              : [defaultItem(0)],
                        })
                      }
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                      Remover item
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-end">
            <div className="rounded-full border border-border bg-elevated px-4 py-2 text-sm font-semibold text-text">
              Total estimado: {formatBRLInput(total)}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-elevated p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Condições de pagamento" htmlFor="orc-pagamento">
              <Input
                id="orc-pagamento"
                value={selectedVersion.condicoes_pagamento}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ condicoes_pagamento: e.target.value })}
              />
            </Field>
            <Field label="Validade da proposta" htmlFor="orc-validade">
              <Input
                id="orc-validade"
                value={selectedVersion.validade_texto}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ validade_texto: e.target.value })}
              />
            </Field>
            <Field label="Prazo de execução" htmlFor="orc-prazo" className="md:col-span-2">
              <Input
                id="orc-prazo"
                value={selectedVersion.prazo_execucao}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ prazo_execucao: e.target.value })}
              />
            </Field>
            <Field label="Exclusões" htmlFor="orc-exclusoes" className="md:col-span-2">
              <textarea
                id="orc-exclusoes"
                className="input min-h-[90px] resize-y rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text"
                value={selectedVersion.exclusoes}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ exclusoes: e.target.value })}
              />
            </Field>
            <Field label="Considerações técnicas" htmlFor="orc-consideracoes" className="md:col-span-2">
              <textarea
                id="orc-consideracoes"
                className="input min-h-[110px] resize-y rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text"
                value={selectedVersion.consideracoes}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ consideracoes: e.target.value })}
              />
            </Field>
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-elevated p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Responsável" htmlFor="orc-resp-nome">
              <Input
                id="orc-resp-nome"
                value={selectedVersion.responsavel_nome}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ responsavel_nome: e.target.value })}
              />
            </Field>
            <Field label="Titulação" htmlFor="orc-resp-titulos">
              <Input
                id="orc-resp-titulos"
                value={selectedVersion.responsavel_titulos}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ responsavel_titulos: e.target.value })}
              />
            </Field>
            <Field label="Registro profissional" htmlFor="orc-resp-registro">
              <Input
                id="orc-resp-registro"
                value={selectedVersion.responsavel_registro}
                disabled={!isEditable}
                onChange={(e) => updateSelectedVersion({ responsavel_registro: e.target.value })}
              />
            </Field>
          </div>
        </section>
      </Stack>
    </PageShell>
  );
}
